import { Parameter } from '@shared/types';

/**
 * Calculates intuitive min and max values for a parameter based on its default value
 * Creates ranges like: 94mm -> 0-100, 2.7 -> 0-10, 0.5 -> 0-1
 * Range is fixed based on default value to prevent runaway expansion
 */
export function calculateParameterRange(param: Parameter): {
  min: number;
  max: number;
} {
  // If explicit range is provided, use it
  if (param.range?.min !== undefined && param.range?.max !== undefined) {
    return { min: param.range.min, max: param.range.max };
  }

  const defaultValue = Number(param.defaultValue);

  // Use only the default value for range calculation to prevent runaway expansion
  const referenceValue = Math.abs(defaultValue);

  // Handle zero or very small values
  if (referenceValue <= 0.001) {
    return { min: 0, max: 1 };
  }

  // Calculate the order of magnitude
  const magnitude = Math.floor(Math.log10(referenceValue));
  const normalizedValue = referenceValue / Math.pow(10, magnitude);

  let rangeMultiplier: number;

  // Determine appropriate range based on normalized value
  // Round up to next nice number: 1, 2, 5, or 10
  if (normalizedValue <= 1) {
    rangeMultiplier = 1;
  } else if (normalizedValue <= 2) {
    rangeMultiplier = 2;
  } else if (normalizedValue <= 5) {
    rangeMultiplier = 5;
  } else {
    rangeMultiplier = 10;
  }

  let maxValue = rangeMultiplier * Math.pow(10, magnitude);

  // Special handling for values that should round up to next nice range
  // e.g., 94 -> 100, 2.7 -> 10 (since 2.7 is closer to 10 than 5)
  if (referenceValue > maxValue * 0.5) {
    if (rangeMultiplier === 1) {
      rangeMultiplier = 2;
    } else if (rangeMultiplier === 2) {
      rangeMultiplier = 5;
    } else if (rangeMultiplier === 5) {
      rangeMultiplier = 10;
    } else {
      rangeMultiplier = 10;
      maxValue = Math.pow(10, magnitude + 1);
    }
    maxValue = rangeMultiplier * Math.pow(10, magnitude);
  }

  // Determine minimum value
  let minValue: number;
  if (param.range?.min !== undefined) {
    minValue = param.range.min;
  } else {
    // For negative default values, use symmetric range
    // For positive default values, start from 0
    if (defaultValue < 0) {
      minValue = -maxValue;
    } else {
      minValue = 0;
    }
  }

  return { min: minValue, max: maxValue };
}

/**
 * Calculates an appropriate step size for a parameter based on its range
 */
export function calculateParameterStep(param: Parameter): number {
  // If explicit step is provided, use it
  if (param.range?.step !== undefined) {
    return param.range.step;
  }

  const { min, max } = calculateParameterRange(param);
  const range = max - min;

  // Handle very small ranges
  if (range <= 0.001) {
    return 0.001;
  }

  // Calculate step as 1% of the range, rounded to a nice number
  const rawStep = range / 100;
  const magnitude = Math.floor(Math.log10(rawStep));
  const normalizedStep = rawStep / Math.pow(10, magnitude);

  let stepMultiplier: number;
  if (normalizedStep <= 1) {
    stepMultiplier = 1;
  } else if (normalizedStep <= 2) {
    stepMultiplier = 2;
  } else if (normalizedStep <= 5) {
    stepMultiplier = 5;
  } else {
    stepMultiplier = 10;
  }

  return stepMultiplier * Math.pow(10, magnitude);
}

/**
 * Determines if a parameter represents a measurement in millimeters
 */
export function isMeasurementParameter(param: Parameter): boolean {
  // Common measurement-related terms
  const measurementTerms = [
    'width',
    'height',
    'length',
    'depth',
    'thickness',
    'radius',
    'diameter',
    'distance',
    'size',
    'offset',
    'gap',
    'spacing',
    'margin',
    'padding',
    'inset',
    'extrude',
    'dimension',
    'inch',
    'in',
  ];

  // Check if parameter name or displayName contains measurement terms
  const nameToCheck = param.name.toLowerCase();
  const displayNameToCheck = param.displayName.toLowerCase();

  // Only consider number type parameters
  if (param.type !== 'number' && param.type !== undefined) {
    return false;
  }

  // Check if the parameter name or display name contains any measurement terms
  return measurementTerms.some(
    (term) => nameToCheck.includes(term) || displayNameToCheck.includes(term),
  );
}

/**
 * Determines the unit of a measurement parameter (mm, in, or empty string)
 * Returns 'in' for inch-based parameters, 'mm' for metric, empty string for non-measurements
 */
export function getParameterUnit(param: Parameter): string {
  // First check if it's a measurement parameter
  if (!isMeasurementParameter(param)) {
    return '';
  }

  const nameToCheck = param.name.toLowerCase();
  const displayNameToCheck = param.displayName.toLowerCase();

  // Check for inch indicators in the parameter name
  // Common patterns: "width_in", "widthIn", "width_inch", "widthInch", "width in", "inch"
  const inchPatterns = [
    /_in$/,        // ends with _in (snake_case)
    /in$/,         // ends with In (camelCase) - but not "in" inside a word
    /_inch/i,      // contains _inch
    /inch/i,       // contains inch
    /\sin$/,       // ends with " in" (display name with space)
    /\sinch/i,     // contains " inch" (display name)
    /^in_/,        // starts with in_
    /^inch/i,      // starts with inch
  ];

  // Check both name and displayName for inch patterns
  const isInchParameter = inchPatterns.some(
    (pattern) => pattern.test(nameToCheck) || pattern.test(displayNameToCheck)
  );

  if (isInchParameter) {
    return 'in';
  }

  // Default to mm for other measurement parameters
  return 'mm';
}

/**
 * Validates and sanitizes a parameter value
 */
export function validateParameterValue(
  param: Parameter,
  value: Parameter['value'],
): Parameter['value'] {
  if (param.type === 'number' || !param.type) {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Number(param.defaultValue);
    }
    return numValue;
  }

  return value;
}

export function updateParameter(code: string, param: Parameter): string {
  const escapedName = escapeRegExp(param.name);
  const regex = new RegExp(
    `^\\s*(${escapedName}\\s*=\\s*)[^;]+;([\\t\\f\\cK ]*\\/\\/[^\n]*)?`,
    'm',
  );
  // Default to assuming the type is number
  if (!param.type) {
    return code.replace(regex, `$1${param.value};$2`);
  }
  switch (param.type) {
    case 'string':
      return code.replace(
        regex,
        `$1"${escapeReplacement(escapeQuotes(param.value as string))}";$2`,
      );
    case 'number':
      return code.replace(regex, `$1${param.value};$2`);
    case 'boolean':
      return code.replace(regex, `$1${param.value};$2`);
    case 'string[]':
      return code.replace(
        regex,
        `$1[${(param.value as string[])
          .map((value) => escapeReplacement(escapeQuotes(value)))
          .map((value) => `"${value}"`)
          .join(',')}];$2`,
      );
    case 'number[]':
      return code.replace(
        regex,
        `$1[${(param.value as number[]).join(',')}];$2`,
      );
    case 'boolean[]':
      return code.replace(
        regex,
        `$1[${(param.value as boolean[]).join(',')}];$2`,
      );
    default:
      return code;
  }
}

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function escapeReplacement(string: string) {
  return string.replace(/\$/g, '$$$$');
}

export function escapeQuotes(string: string) {
  return string.replace(/"/g, '\\"');
}
